/* eslint-disable react-hooks/exhaustive-deps */
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import React, { useMemo, useState, useEffect } from "react";
import anime from "animejs";
import throttle from "lodash-es/throttle";
import { RootState, AppDispatch } from "app/store";
import I18n from "common/i18n";
import { GameCaseManager } from "common/elements";
import Constants, { BASE_HEIGHT, BASE_WIDTH } from "common/constant";
import { sleep, btnDelay, useScale, usePlaySound, isDebugMode } from "common/utils";
import { Course } from "features/courses/types";
import { actions } from "features/course/slice";
import { CardBg } from "features/creator/assets";
import { BackgroundSvg } from "features/home/assets";
import { AllStageClear } from "features/courses/assets";
import { actions as coursesActions } from "features/courses/coursesSlice";
import { PieceBuilderStartPage, PieceBuilderTopPage } from "features/builder";
import { SokobanStartPage, SokobanLevelSelectPage } from "features/courses/sokoban";
import { ProgramStagePage, ProgramStageStartPage } from "features/courses/program/stages";
import { AlgorithmStagePage, AlgorithmStageStartPage } from "features/courses/algorithm/stages";
import {
  allProgramStageClearAnimation,
  allAlgorithmStageClearAnimation,
} from "features/courses/api";
import {
  AlgorithmTangibleStagePage,
  AlgorithmTangibleStageStartPage,
} from "features/courses/algorithm_tangible/stages";
import { DefaultStartPage } from "./DefaultStartPage";

const AllStageClearComponent = React.memo(() => {
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();
  const active = useSelector((state: RootState) => state.user.active);

  const algorithmSummary = useSelector(
    (state: RootState) => state.courses.progresses.algorithm.summary,
  );
  const algorithmStageProgresses = Math.floor(
    (algorithmSummary.finished / algorithmSummary.total) * 100,
  );
  const isAllAlgorithmStageClearAnimation =
    !algorithmSummary.all_stage_clear_animation && algorithmStageProgresses === 100;
  const [allAlgorithmStageClear, setAllAlgorithmStageClear] = useState(
    isAllAlgorithmStageClearAnimation,
  );

  const programSummary = useSelector(
    (state: RootState) => state.courses.progresses.program.summary,
  );
  const programStageProgresses = Math.floor((programSummary.finished / programSummary.total) * 100);
  const isAllProgramStageClearAnimation =
    !programSummary.all_stage_clear_animation && programStageProgresses === 100;
  const [allProgramStageClear, setAllProgramStageClear] = useState(isAllProgramStageClearAnimation);

  const handleCloseAllStageClear = () =>
    btnDelay(() => {
      play();
      setAllProgramStageClear(false);
      setAllAlgorithmStageClear(false);
    });

  useEffect(() => {
    if (allAlgorithmStageClear) {
      dispatch(coursesActions.allAlgorithmStageClearAnimation());
      allAlgorithmStageClearAnimation({
        uid: active.uid,
      });
    }
  }, [allAlgorithmStageClear]);

  useEffect(() => {
    if (allProgramStageClear) {
      dispatch(coursesActions.allProgramStageClearAnimation());
      allProgramStageClearAnimation({
        uid: active.uid,
      });
    }
  }, [allProgramStageClear]);

  return allAlgorithmStageClear || allProgramStageClear ? (
    <AllStageClear close={handleCloseAllStageClear} />
  ) : (
    <></>
  );
});

const CourseCard = ({ onClick }: { onClick: () => void }) => (
  <div
    onClick={onClick}
    className="flex-col-el flex-center w-[160px] cursor-pointer pointer-events-auto"
  >
    <div className="flex-col-center rounded-[16px] p-2">
      <div className="flex-col-el flex-center w-[160px] h-[174px] absolute -z-0">
        <CardBg />
      </div>

      <div className="flex-col-center w-[144px] h-[176px] ml-4">
        <div className="flex-col-el flex-center absolute w-[62px] h-[64px]">
          <img
            alt="switch"
            className="w-full h-full"
            src={`${Constants.assetHost}/assets/images/project_card_switch.png`}
          />
        </div>
      </div>
    </div>
  </div>
);

const SoftCardComponent = ({
  scale,
  selectedCourse,
  setSelectedCourse,
}: {
  scale: number;
  selectedCourse: Course;
  setSelectedCourse: (course: Course) => void;
}) => {
  const SOFT_CARD_WIDTH = 136;
  const SOFT_CARD_HEIGHT = 168;
  const SOFT_CARD_OFFSET = 480;
  const SOFT_CARD_MARGIN = 40;
  const play = usePlaySound();
  const dispatch = useDispatch();
  const [animation, setAnimation] = useState(false);
  const [rumAnimation, setRumAnimation] = useState(false);
  const parentConfig = useSelector((state: RootState) => state.config.parentConfig);

  useEffect(() => {
    if (selectedCourse && selectedCourse !== Course.ALGORITHM) {
      const currentSelectedCourseTarget = document.getElementById(
        `courses-soft-card-${selectedCourse}`,
      );
      const currentSelectedCourseTargetIndex = currentSelectedCourseTarget?.dataset.index;
      currentSelectedCourseTarget.style.transform = `translateX(${
        (SOFT_CARD_OFFSET +
          (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * Number(currentSelectedCourseTargetIndex)) *
        scale
      }px) translateY(0px)`;
      currentSelectedCourseTarget.style.transform = `translateX(${
        120 * scale
      }px) translateY(${159 * scale}px)`;
    }
  }, [scale]);

  const handleAnimation = useMemo(
    () =>
      throttle(
        () => {
          toast.warn(
            <div>
              <p className="text text-textcolor-black">
                {I18n.t("MSG_COURSE_COMING_SOON_WARNING_MESSAGE")}
              </p>
            </div>,
          );
          setRumAnimation(true);
        },
        2000,
        { trailing: false },
      ),

    [],
  );

  const handleSetSelectCourse = useMemo(
    () =>
      throttle(
        (course: Course) => {
          setSelectedCourse(course);
        },
        1000,
        { trailing: false },
      ),

    [],
  );

  const handleSelectCourse = (course: Course) => async () => {
    if (animation) return;

    play();

    setAnimation(true);

    const currentSelectedCourseTarget = document.getElementById(
      `courses-soft-card-${selectedCourse}`,
    );
    const currentSelectedCourseTargetIndex = currentSelectedCourseTarget?.dataset.index;

    if (selectedCourse !== course) {
      const clickedCourseTarget = document.getElementById(`courses-soft-card-${course}`);

      anime({
        targets: currentSelectedCourseTarget,
        opacity: [1, 0],
        duration: 800,
        easing: "easeInOutQuad",
      }).finished.then(async () => {
        await anime({
          targets: currentSelectedCourseTarget,
          translateX:
            (SOFT_CARD_OFFSET +
              (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * Number(currentSelectedCourseTargetIndex)) *
            scale,
          translateY: 0,
          duration: 10,
          easing: "easeInOutQuad",
        }).finished;
        await anime({
          targets: currentSelectedCourseTarget,
          opacity: [0, 1],
          duration: 800,
          easing: "easeInOutQuad",
        }).finished;
      });

      anime({
        targets: clickedCourseTarget,
        opacity: [1, 0],
        duration: 800,
        easing: "easeInOutQuad",
      }).finished.then(async () => {
        await anime({
          targets: clickedCourseTarget,
          translateX: 120 * scale,
          translateY: 0,
          duration: 10,
          easing: "easeInOutQuad",
        }).finished;
        anime({
          targets: clickedCourseTarget,
          opacity: [0, 1],
          translateY: 159 * scale,
          duration: 800,
          easing: "easeInOutQuad",
        });

        await anime({
          targets: document.getElementById("course-stage-list"),
          scaleX: [1, 0.2, 0.2],
          scaleY: [0.002, 0.002, 0.002],
          duration: 500,
          autoplay: true,
          easing: "easeInOutQuad",
        }).finished;

        handleSetSelectCourse(course);
        dispatch(actions.updateShowCourseTop(true));
        dispatch(actions.updateShowCourseList(false));

        await sleep(100);

        await anime({
          targets: document.getElementById("course-stage-list"),
          opacity: [0, 1],
          scaleX: [0.3, 1],
          scaleY: [0.3, 1],
          duration: 400,
          autoplay: true,
          easing: "easeInOutQuad",
        }).finished;

        setAnimation(false);
      });
    } else {
      anime({
        targets: currentSelectedCourseTarget,
        opacity: [1, 0],
        duration: 800,
        easing: "easeInOutQuad",
      }).finished.then(async () => {
        await anime({
          targets: currentSelectedCourseTarget,
          translateX:
            (SOFT_CARD_OFFSET +
              (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * Number(currentSelectedCourseTargetIndex)) *
            scale,
          translateY: 0,
          duration: 10,
          easing: "easeInOutQuad",
        }).finished;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        anime({
          targets: currentSelectedCourseTarget,
          opacity: [0, 1],
          duration: 800,
          easing: "easeInOutQuad",
        }).finished;

        await anime({
          targets: document.getElementById("course-stage-list"),
          scaleX: [1, 0.2, 0.2],
          scaleY: [0.002, 0.002, 0.002],
          duration: 500,
          autoplay: true,
          easing: "easeInOutQuad",
        }).finished;

        handleSetSelectCourse(null);
        dispatch(actions.updateShowCourseTop(true));
        dispatch(actions.updateShowCourseList(false));

        await sleep(100);

        await anime({
          targets: document.getElementById("course-stage-list"),
          opacity: [0, 1],
          scaleX: [0.3, 1],
          scaleY: [0.3, 1],
          duration: 400,
          autoplay: true,
          easing: "easeInOutQuad",
        }).finished;

        setAnimation(false);
      });
    }
  };

  const handleClickComingSoon = () => {
    play();
    handleAnimation();
  };

  return (
    <div
      className="flex-row-el items-center justify-start relative"
      style={{ width: BASE_WIDTH * scale, height: 250 * scale }}
    >
      <div
        style={{
          width: 96 * scale,
          height: 80 * scale,
          left: 250 * scale,
          bottom: 73 * scale,
        }}
        className="flex-col-el flex-center absolute"
      >
        <img
          alt="コースの選択指示装飾"
          src={`${Constants.assetHost}/assets/images/course_select_decoration.png`}
          className="w-full h-full"
        />
      </div>

      <div
        style={{
          transform: `translateX(${(SOFT_CARD_OFFSET - 28) * scale}px) scale(${scale})`,
        }}
        className="flex-row-el absolute origin-left"
      >
        <div className="flex-row-el items-center justify-start relative px-2 rounded-2xl bg-orange-10 overflow-hidden mt-[9px] border-solid border-b-[10px] border-[1px] border-[#E5B876] border-b-[#E1AD60]">
          <div className="flex-row-el gap-4">
            {Array(3 + (parentConfig?.enable_lab ? 1 : 0) + (isDebugMode ? 1 : 0))
              .fill(0)
              .map((_, index) => (
                <CourseCard key={index} onClick={handleSelectCourse(selectedCourse)} />
              ))}
          </div>
        </div>
      </div>

      <div
        data-index={0}
        id="courses-soft-card-algorithm"
        onClick={handleSelectCourse(Course.ALGORITHM)}
        className="flex-col-el flex-center absolute cursor-pointer will-change-transform"
        style={{
          width: SOFT_CARD_WIDTH * scale,
          height: SOFT_CARD_HEIGHT * scale,
          transform: `translateX(${SOFT_CARD_OFFSET * scale}px) translateY(0px)`,
        }}
      >
        <img
          alt="アルゴリズム学ぶソフト"
          src={`${Constants.assetHost}/assets/images/course_soft_algorithm.png`}
          className="w-full h-full"
        />
      </div>

      {parentConfig?.enable_lab && (
        <div
          data-index={1}
          id="courses-soft-card-algorithm_tangible"
          className="flex-col-center !absolute cursor-pointer will-change-transform"
          style={{
            width: SOFT_CARD_WIDTH * scale,
            height: SOFT_CARD_HEIGHT * scale,
            transform: `translateX(${
              (SOFT_CARD_OFFSET + (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN)) * scale
            }px) translateY(0px)`,
          }}
          onClick={handleSelectCourse(Course.ALGORITHM_TANGIBLE)}
        >
          <img
            alt="アルゴリズム学ぶソフト"
            src={`${Constants.assetHost}/assets/images/course_soft_algorithm_tangible.png`}
            className="w-full h-full"
          />
        </div>
      )}

      <div
        data-index={parentConfig?.enable_lab ? 2 : 1}
        id="courses-soft-card-program"
        onClick={handleSelectCourse(Course.PROGRAM)}
        className="flex-col-el flex-center absolute cursor-pointer will-change-transform"
        style={{
          width: SOFT_CARD_WIDTH * scale,
          height: SOFT_CARD_HEIGHT * scale,
          transform: `translateX(${
            (SOFT_CARD_OFFSET +
              (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * (parentConfig?.enable_lab ? 2 : 1)) *
            scale
          }px) translateY(0px)`,
        }}
      >
        <img
          alt="プログラム学ぶソフト"
          src={`${Constants.assetHost}/assets/images/course_soft_program.png`}
          className="w-full h-full"
        />
      </div>

      {isDebugMode && (
        <div
          data-index={parentConfig?.enable_lab ? 3 : 2}
          id="courses-soft-card-sokoban"
          onClick={handleSelectCourse(Course.SOKOBAN)}
          className="flex-col-el flex-center absolute cursor-pointer will-change-transform"
          style={{
            width: SOFT_CARD_WIDTH * scale,
            height: SOFT_CARD_HEIGHT * scale,
            transform: `translateX(${
              (SOFT_CARD_OFFSET +
                (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * (parentConfig?.enable_lab ? 3 : 2)) *
              scale
            }px) translateY(0px)`,
          }}
        >
          <img
            alt="倉庫番ソフト"
            src={`${Constants.assetHost}/assets/images/course_soft_sokoban.png`}
            className="w-full h-full"
          />
        </div>
      )}

      {isDebugMode && (
        <div
          data-index={parentConfig?.enable_lab ? 4 : 3}
          id="courses-soft-card-piece_builder"
          onClick={handleSelectCourse(Course.PIECE_BUILDER)}
          className="flex-col-el flex-center absolute cursor-pointer will-change-transform"
          style={{
            width: SOFT_CARD_WIDTH * scale,
            height: SOFT_CARD_HEIGHT * scale,
            transform: `translateX(${
              (SOFT_CARD_OFFSET +
                (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * (parentConfig?.enable_lab ? 4 : 3)) *
              scale
            }px) translateY(0px)`,
          }}
        >
          <img
            alt="ピースビルダーソフト"
            src={`${Constants.assetHost}/assets/images/course_soft_builder.png`}
            className="w-full h-full"
          />
        </div>
      )}

      {!isDebugMode && (
        <div
          className="flex-col-el flex-center absolute cursor-pointer"
          onClick={handleClickComingSoon}
          style={{
            width: SOFT_CARD_WIDTH * scale,
            height: SOFT_CARD_HEIGHT * scale,
            transform: `translateX(${
              (SOFT_CARD_OFFSET +
                (SOFT_CARD_WIDTH + SOFT_CARD_MARGIN) * ((parentConfig?.enable_lab ? 1 : 0) + 2)) *
              scale
            }px) translateY(0px)`,
          }}
        >
          <div
            className={`flex-col-el flex-center absolute ${rumAnimation && "rum"}`}
            style={{
              width: SOFT_CARD_WIDTH * scale,
              height: SOFT_CARD_HEIGHT * scale,
            }}
            onAnimationEnd={() => setRumAnimation(false)}
          >
            <img
              alt="コースでまなぶのComing Soon"
              src={`${Constants.assetHost}/assets/images/course_soft_coming_soon.png`}
              className="absolute"
              style={{
                width: SOFT_CARD_WIDTH * scale,
                height: SOFT_CARD_HEIGHT * scale,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CourseStageList = () => {
  const selectedCourse = useSelector((state: RootState) => state.course.selectedCourse);
  const show = useSelector((state: RootState) => state.course.showCourseList);

  return (
    <div className="w-full h-full absolute">
      {selectedCourse === Course.ALGORITHM && show && <AlgorithmStagePage />}
      {selectedCourse === Course.PROGRAM && show && <ProgramStagePage />}
      {selectedCourse === Course.ALGORITHM_TANGIBLE && show && <AlgorithmTangibleStagePage />}
      {selectedCourse === Course.SOKOBAN && show && <SokobanLevelSelectPage />}
      {selectedCourse === Course.PIECE_BUILDER && show && <PieceBuilderTopPage />}
    </div>
  );
};

const CourseStageComponent = ({
  scale,
  selectedCourse,
}: {
  scale: number;
  selectedCourse: Course;
}) => {
  const showCourseTop = useSelector((state: RootState) => state.course.showCourseTop);
  const config = useSelector((state: RootState) => state.config.userConfig);
  const GameCaseComponent = useMemo(() => GameCaseManager[config.game_case], [config.game_case]);

  useEffect(() => {
    anime({
      targets: document.getElementById("course-stage-list"),
      scaleX: [0.3, 1],
      scaleY: [0.3, 1],
      duration: 200,
      autoplay: true,
      easing: "easeInOutQuad",
    });
  }, []);

  return (
    <div
      style={{
        width: 1216 * scale,
        height: 646 * scale,
      }}
      className="flex-col-center"
    >
      <div className="flex-col-center" style={{ transform: `scale(${scale})` }}>
        <div className="flex-col-el w-[1126px] h-[574px] absolute top-[38px] left-[45px] z-[100] pointer-events-none">
          <div className="flex-col-el w-full h-full rounded-[17px] border-solid border-[1px] border-[#000000]/40 p-1">
            <div className="flex-col-el w-full h-full rounded-[12px] border-solid border-[2px] border-[#000000]/30 overflow-hidden">
              <div className="flex-col-el w-full h-full rounded-[10px] border-solid border-[4px] border-[#FFFFFF]/40" />
            </div>
          </div>
        </div>

        <div className="flex-col-el flex-center w-[1124px] h-[572px] absolute top-[39px] left-[46px] border-solid border-gray-100 border-[6px] rounded-[16px] overflow-hidden z-50">
          <div className="flex-col-center w-full h-full bg-gray2-100 !absolute top-0 left-0" />

          <div className="flex-col-center w-[1118px] h-[566px] !absolute top-0 left-0 scale-x-100 scale-y-100">
            <div
              id="course-stage-list"
              className="w-full h-full absolute z-[100] will-change-transform"
            >
              {!selectedCourse && <DefaultStartPage />}
              {selectedCourse === Course.ALGORITHM && showCourseTop && <AlgorithmStageStartPage />}
              {selectedCourse === Course.PROGRAM && showCourseTop && <ProgramStageStartPage />}
              {selectedCourse === Course.ALGORITHM_TANGIBLE && showCourseTop && (
                <AlgorithmTangibleStageStartPage />
              )}
              {selectedCourse === Course.SOKOBAN && showCourseTop && <SokobanStartPage />}
              {selectedCourse === Course.PIECE_BUILDER && showCourseTop && (
                <PieceBuilderStartPage />
              )}
              <CourseStageList />
            </div>
          </div>
        </div>

        <div
          className="flex-col-center w-[1216px] h-[646px]"
          style={{
            filter: "drop-shadow(0px 0px 3px rgba(63, 74, 97, 0.3))",
          }}
        >
          <img
            alt="*"
            src={`${Constants.assetHost}/assets/images/${GameCaseComponent}`}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
};

export const CourseTopPage = () => {
  const dispatch = useDispatch();
  const { scale, width, height } = useScale();
  const scaleX = width / BASE_WIDTH;
  const scaleY = height / BASE_HEIGHT;
  const defaultSelectedCourse = useSelector((state: RootState) => state.course.selectedCourse);
  const [selectedCourse, _setSelectedCourse] = useState(defaultSelectedCourse);
  const setSelectedCourse = (course: Course) => {
    dispatch(actions.updateSelectedCourse({ course }));
    _setSelectedCourse(course);
  };

  return (
    <div className="flex-col-el w-full h-full">
      <img
        alt="*"
        src={`${Constants.assetHost}/assets/images/new_bg_puzzle.png`}
        className="absolute inset-0 object-cover w-full h-full z-[-1]"
      />
      <AllStageClearComponent />
      <div className="flex-col-el flex-center flex-1 w-full h-full">
        <SoftCardComponent
          scale={scale}
          selectedCourse={selectedCourse}
          setSelectedCourse={setSelectedCourse}
        />
        <CourseStageComponent scale={scale} selectedCourse={selectedCourse} />
      </div>

      <div
        className="flex-col-center !absolute bottom-0 z-[-1]"
        style={{
          width: width,
          height: 235 * scaleY,
        }}
      >
        <div
          className="flex-col-center w-[1280px] h-[235px]"
          style={{ transform: `scale(${scaleX}, ${scaleY})` }}
        >
          <BackgroundSvg />
        </div>
      </div>
    </div>
  );
};
